import type { ReactNode } from 'react';

import betaLogo from '../assets/brand/beta-project-logo.png';
import eisenhardt34c from '../assets/brand/eisenhardt-34c-gewo.jpg';
import pfau34c from '../assets/brand/pfau-34c-gewo.jpg';
import type { ContractDesignMode } from './contractRules';

// Erlaubnisurkunden nach § 34c GewO als Anlage – erkannt am Aktenzeichen bzw. Vertragspartner (Gegenstück: documents/pack.go).
const certificates34c: { match: (erlaubnis: string, partner: string) => boolean; src: string }[] = [
  { match: (e, p) => e.includes('Ord A 110') || p.includes('BETA Project'), src: pfau34c },
  { match: (e, p) => e.includes('2211-121.271') || p.includes('Dominik Eisenhardt'), src: eisenhardt34c },
];

function MaklerpassWordmark({ gold = false, height = 24 }: { gold?: boolean; height?: number }) {
  const stroke = gold ? '#D4B062' : '#142238';
  const sq = gold ? '#D4B062' : '#9C7A32';
  const textFill = gold ? '#F4F1EA' : '#142238';
  const width = Math.round(height * 7.5);
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 270 36"
      width={width}
      height={height}
      role="img"
      aria-label="Maklerpass"
      style={{ display: 'block', height: `${height}px`, width: 'auto' }}
    >
      <g transform="scale(0.3714)">
        <g fill="none" stroke={stroke} strokeWidth="8" strokeLinecap="square">
          <path d="M8 52 L70 6 L132 52" />
          <path d="M26 40 V94" />
          <path d="M114 40 V94" />
        </g>
        <rect x="58" y="44" width="24" height="24" fill={sq} />
      </g>
      <text
        x="68"
        y="27"
        fill={textFill}
        fontFamily="'Dax Pro', 'Barlow', system-ui, sans-serif"
        fontWeight="700"
        fontSize="24"
        letterSpacing="3.2"
      >
        MAKLERPASS
      </text>
    </svg>
  );
}

function contractTypeLabel(templateKey?: string): { badge: string; sub: string } {
  switch (templateKey) {
    case 'maklervertrag_qualifiziert':
      return { badge: 'Qualifizierter Alleinauftrag', sub: 'individuell vereinbart' };
    case 'maklervertrag_alleinauftrag':
      return { badge: 'Einfacher Alleinauftrag', sub: 'Alleinauftrag' };
    case 'maklervertrag_einfach':
      return { badge: 'Allgemeiner Maklerauftrag', sub: 'Allgemeiner Auftrag' };
    default:
      return { badge: 'Maklerauftrag', sub: '' };
  }
}

// DocumentSheet: DIN-A4-Blatt im Maklerpass-Design („Leder“, „Der Pass“ oder „Klassisch“ aus Maklervertrag neu formatiert).
export function DocumentSheet({
  context,
  templateKey,
  values,
  design = 'leder',
  children,
}: {
  context: Record<string, string>;
  templateKey?: string;
  values?: Record<string, string>;
  design?: ContractDesignMode;
  children: ReactNode;
}) {
  const isContract = Boolean(templateKey && templateKey.startsWith('maklervertrag_'));
  const contact = [context['makler.telefon'], context['makler.email']].filter(Boolean).join(' · ');
  const website = (context['makler.website'] ?? '').replace(/^https?:\/\//, '');
  const partner = context['makler.vertragspartner'] || context['makler.firma'];
  const cert34c = certificates34c.find((c) => c.match(context['makler.erlaubnis'] ?? '', partner ?? ''))?.src;
  const footerMeta = [
    partner && partner !== context['makler.firma'] ? `Vertragspartner: ${partner}` : '',
    context['makler.register'],
    context['makler.ust_id'] && `USt-IdNr. ${context['makler.ust_id']}`,
    context['makler.aufsicht'] && `Aufsicht: ${context['makler.aufsicht']}`,
  ]
    .filter(Boolean)
    .join(' · ');

  const objektArt = values?.['objektart'] || 'Immobilie';
  const objektAdr = values?.['objekt_adresse'] || context['lead.adresse'] || '';
  const shortAdr = objektAdr.split(',')[0] || objektAdr;
  const preisRaw = values?.['angebotspreis'] || '';
  const preisFormatted = preisRaw ? (preisRaw.includes('€') ? preisRaw : `${preisRaw} €`) : 'nach Vereinbarung';
  const laufzeit =
    templateKey === 'maklervertrag_einfach'
      ? 'Unbefristet'
      : values?.['laufzeit_monate']
        ? `${values['laufzeit_monate']} Monate`
        : '6 Monate';
  const provModell = values?.['provisionsmodell'] || 'Halbteilung';
  const { badge, sub } = contractTypeLabel(templateKey);

  return (
    <article
      className={`doc-sheet doc-print ${isContract ? `doc-contract doc-design-${design}` : ''}`}
      aria-label="Vorschau"
    >
      {isContract && design === 'beta' ? (
        <>
          <header className="doc-letterhead" style={{ justifyContent: 'flex-end', borderBottom: '2px solid #C59B27' }}>
            <div className="doc-letterhead-info" style={{ alignItems: 'flex-end' }}>
              <img src={betaLogo} alt="BETA Project & Immobilien GmbH" width={145} height={61} style={{ height: '48px', width: 'auto', marginBottom: '4px' }} />
              <strong>BETA Project &amp; Immobilien GmbH</strong>
              <span>{context['makler.anschrift'] || 'Joachim-Friedrich-Straße 10A · 10711 Berlin'}</span>
              <span>{contact || 'T: 0151 – 74 20 41 46 · E-Mail: kontakt@beta-project-immobilien.de'}</span>
            </div>
          </header>
          <section className="mv-classic-summary" aria-label="Exklusives Vermarktungsmandat Eckdaten" style={{ borderLeft: '4px solid #1F4E79' }}>
            <div className="mv-strip-cell">
              <span className="mv-party-kicker" style={{ color: '#1F4E79' }}>Vermarktungsprofil</span>
              <span className="mv-strip-val">
                Exklusives Vermarktungsmandat · {objektArt}
                {shortAdr ? `, ${shortAdr}` : ''}
              </span>
            </div>
            <div className="mv-strip-cell">
              <span className="mv-party-kicker" style={{ color: '#1F4E79' }}>Angebotspreis</span>
              <span className="mv-pass-price">{preisFormatted}</span>
            </div>
            <div className="mv-strip-cell">
              <span className="mv-party-kicker" style={{ color: '#1F4E79' }}>Laufzeit</span>
              <span className="mv-strip-val">{laufzeit}</span>
            </div>
            <div className="mv-strip-cell">
              <span className="mv-party-kicker" style={{ color: '#1F4E79' }}>Provision</span>
              <span className="mv-strip-val">{provModell}</span>
            </div>
          </section>
        </>
      ) : isContract && design === 'leder' ? (
        <header className="mv-leder-header">
          <div className="mv-leder-top">
            <div className="mv-leder-brand">
              <MaklerpassWordmark gold height={24} />
              <span className="mv-standort-label">{context['makler.firma']}</span>
            </div>
            <div className="mv-leder-contact">
              {context['makler.anschrift'] && <span>{context['makler.anschrift']}</span>}
              {contact && <span>{contact}</span>}
              {website && <span>{website}</span>}
            </div>
          </div>
          <div className="mv-leder-title-block">
            <div className="mv-leder-h1">Maklervertrag – Verkauf</div>
            <div className="mv-leder-badges">
              <span className="mv-badge-gold">{badge}</span>
              {sub && <span className="mv-badge-sub">{sub}</span>}
            </div>
          </div>
          <div className="mv-leder-strip">
            <div className="mv-strip-cell">
              <span className="mv-strip-label">Objekt</span>
              <span className="mv-strip-val">
                {objektArt}
                {shortAdr ? (
                  <>
                    <br />
                    {shortAdr}
                  </>
                ) : null}
              </span>
            </div>
            <div className="mv-strip-cell">
              <span className="mv-strip-label">Angebotspreis</span>
              <span className="mv-strip-price">{preisFormatted}</span>
            </div>
            <div className="mv-strip-cell">
              <span className="mv-strip-label">Laufzeit</span>
              <span className="mv-strip-val">{laufzeit}</span>
            </div>
            <div className="mv-strip-cell">
              <span className="mv-strip-label">Provision</span>
              <span className="mv-strip-val">{provModell}</span>
            </div>
          </div>
        </header>
      ) : isContract && design === 'pass' ? (
        <>
          <div className="mv-pass-eyebrow">
            <span>Maklervertrag — Verkauf</span>
            <span>{context['heute'] || ''}</span>
          </div>
          <header className="doc-letterhead mv-pass-header">
            <div className="doc-letterhead-brand">
              <MaklerpassWordmark height={28} />
            </div>
            <div className="doc-letterhead-info">
              <strong>{context['makler.firma']}</strong>
              {context['makler.anschrift'] && <span>{context['makler.anschrift']}</span>}
              {[contact, website].filter(Boolean).join(' · ') && <span>{[contact, website].filter(Boolean).join(' · ')}</span>}
            </div>
          </header>
          <section className="mv-pass-combined-card" aria-label="Eckdaten">
            <div className="mv-pass-strip">
              <div className="mv-strip-cell">
                <span className="mv-party-kicker">Objekt</span>
                <span className="mv-strip-val">
                  {objektArt}
                  {shortAdr ? `, ${shortAdr}` : ''}
                </span>
              </div>
              <div className="mv-strip-cell">
                <span className="mv-party-kicker">Angebotspreis</span>
                <span className="mv-pass-price">{preisFormatted}</span>
              </div>
              <div className="mv-strip-cell">
                <span className="mv-party-kicker">Laufzeit</span>
                <span className="mv-strip-val">{laufzeit}</span>
              </div>
              <div className="mv-strip-cell">
                <span className="mv-party-kicker">Provision</span>
                <span className="mv-strip-val">{provModell}</span>
              </div>
            </div>
          </section>
        </>
      ) : (
        <>
          <header className="doc-letterhead">
            <div className="doc-letterhead-brand">
              <MaklerpassWordmark height={26} />
            </div>
            <div className="doc-letterhead-info">
              <strong>{context['makler.firma']}</strong>
              {context['makler.anschrift'] && <span>{context['makler.anschrift']}</span>}
              {[contact, website].filter(Boolean).join(' · ') && <span>{[contact, website].filter(Boolean).join(' · ')}</span>}
            </div>
          </header>
          {isContract && (
            <section className="mv-classic-summary" aria-label="Eckdaten">
              <div className="mv-strip-cell">
                <span className="mv-party-kicker">Objekt</span>
                <span className="mv-strip-val">
                  {objektArt}
                  {shortAdr ? `, ${shortAdr}` : ''}
                </span>
              </div>
              <div className="mv-strip-cell">
                <span className="mv-party-kicker">Angebotspreis</span>
                <span className="mv-pass-price">{preisFormatted}</span>
              </div>
              <div className="mv-strip-cell">
                <span className="mv-party-kicker">Laufzeit</span>
                <span className="mv-strip-val">{laufzeit}</span>
              </div>
              <div className="mv-strip-cell">
                <span className="mv-party-kicker">Provision</span>
                <span className="mv-strip-val">{provModell}</span>
              </div>
            </section>
          )}
        </>
      )}
      <div className="doc-body">
        {children}
        {cert34c && (
          <section className="doc-appendix" aria-label="Nachweis nach § 34c GewO" style={{ marginTop: '18px' }}>
            <div className="doc-page-break" />
            <h2>Anlage: Nachweis der Erlaubnis nach § 34c GewO</h2>
            <p className="muted small">
              {[partner, context['makler.anschrift'], context['makler.erlaubnis']].filter(Boolean).join(' · ')}
            </p>
            <img
              src={cert34c}
              alt="Erlaubnisurkunde nach § 34c GewO"
              style={{
                maxWidth: '100%',
                maxHeight: '210mm',
                objectFit: 'contain',
                border: '1px solid #E0D8C8',
                borderRadius: '4px',
                marginTop: '8px',
              }}
            />
          </section>
        )}
      </div>
      <footer className="doc-footer mv-footer-bar">
        <span className="mv-footer-left">
          Maklerpass · Eine Marke der Dolisa{shortAdr ? ` · Maklervertrag ${shortAdr}` : ''}
          {footerMeta ? ` · ${footerMeta}` : ''}
        </span>
        <span className="mv-footer-paraphe">
          Paraphe <i />
        </span>
      </footer>
    </article>
  );
}
